/* Fail-open scroll entrances. Content is visible unless an animation is running. */
(() => {
  'use strict';
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const number = (style, name, fallback, min, max) => {
    const value = parseFloat(style.getPropertyValue(name));
    return Math.max(min, Math.min(max, Number.isFinite(value) ? value : fallback));
  };
  function mount(root = document) {
    const nodes = [...root.querySelectorAll('[data-reveal]')];
    if (root.matches?.('[data-reveal]')) nodes.unshift(root);
    const pending = new Set(nodes);
    const running = new Set();
    const listeners = [];
    let frame = 0, observer = null, disposed = false;
    const on = (target, event, handler, options) => {
      target.addEventListener(event, handler, options);
      listeners.push(() => target.removeEventListener(event, handler, options));
    };
    const finish = () => {
      running.forEach(animation => animation.cancel());
      running.clear();
    };
    function enter(node, animate = true) {
      if (!pending.delete(node)) return;
      observer?.unobserve(node);
      node.classList.add('is-in');
      if (!animate || motion.matches || node.dataset.reveal === 'none' || !node.animate) return;
      const style = getComputedStyle(node);
      const duration = number(style, '--reveal-duration', 1000, 400, 1600);
      const stagger = number(style, '--reveal-stagger', 80, 0, 200);
      const distance = number(style, '--reveal-distance', 32, 8, 64) * (innerWidth < 750 ? 0.75 : 1);
      const isMedia = node.matches('.a7-media,.a7-split-visual') || node.firstElementChild?.matches('.a7-media');
      const children = node.matches('.a7-content,.a7-values__title,.a7-copy')
        ? [...node.children].filter(child => !child.matches('script,style,[hidden]')) : [node];
      const order = Number(node.dataset.revealOrder || 0);
      (children.length ? children : [node]).forEach((child, index) => {
        const from = {opacity: 0, transform: `translate3d(0,${distance}px,0)`};
        const to = {opacity: getComputedStyle(child).opacity, transform: 'translate3d(0,0,0)'};
        if (isMedia) {
          from.transform = `translate3d(0,${distance * 0.6}px,0) scale(1.025)`;
          to.transform = 'translate3d(0,0,0) scale(1)';
        }
        if (node.dataset.reveal === 'mask') {
          from.clipPath = 'inset(0 100% 0 0)';
          to.clipPath = 'inset(0 0% 0 0)';
        }
        try {
          const animation = child.animate([from, to], {
            duration, delay: Math.min((index + order) * stagger, 480),
            easing: 'cubic-bezier(0.22,1,0.36,1)', fill: 'backwards'
          });
          running.add(animation);
          animation.finished.then(() => running.delete(animation), () => running.delete(animation));
        } catch (_) { /* Unsupported animation must never hide the content. */ }
      });
    }
    function scan() {
      frame = 0;
      if (disposed) return;
      pending.forEach(node => {
        if (!node.isConnected) { pending.delete(node); observer?.unobserve(node); return; }
        const rect = node.getBoundingClientRect();
        if (rect.bottom <= 0) enter(node, false);
        else if (rect.top < innerHeight - 24) enter(node);
      });
    }
    const schedule = () => { if (!frame && !disposed) frame = requestAnimationFrame(scan); };
    const showAll = () => { finish(); [...pending].forEach(node => enter(node, false)); };
    if (motion.matches || document.documentElement.classList.contains('shopify-design-mode')) {
      showAll();
      return () => { disposed = true; finish(); };
    }
    // Observe visible layout boxes, never elements hidden by a persistent clip-path.
    if ('IntersectionObserver' in window) {
      try {
        observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (entry.isIntersecting) enter(entry.target);
        }), {threshold: 0, rootMargin: '0px 0px -24px 0px'});
        pending.forEach(node => observer.observe(node));
      } catch (_) { observer = null; }
    }
    // Also covers deep links, fast scrolling, restored scroll positions and observer failures.
    on(window, 'scroll', schedule, {passive: true});
    on(window, 'resize', schedule, {passive: true});
    on(window, 'pageshow', schedule);
    on(window, 'beforeprint', showAll);
    on(document, 'visibilitychange', () => { if (document.hidden) finish(); else schedule(); });
    on(motion, 'change', () => { if (motion.matches) showAll(); });
    on(root, 'focusin', event => { const node = event.target.closest?.('[data-reveal]'); if (node) enter(node, false); });
    on(root, 'shopify:section:select', showAll);
    on(root, 'shopify:block:select', showAll);
    schedule();
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      observer?.disconnect();
      finish();
      listeners.forEach(remove => remove());
      pending.clear();
    };
  }
  window.A7Reveal = {mount};
})();
